import React, {
  useState, useEffect, useMemo, useCallback
} from 'react';
import _ from "lodash";
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { MdDeleteForever } from 'react-icons/md';
import { ToastContainer, toast, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.minimal.css';
import { useGlobalContext } from '../../../GlobalContext';
import {
  getProductByID,
  postProduct,
  putProduct,
  resetSelectedProduct,
  resetErrorProducts,
  deleteProduct,
} from '../../../../actions/products';
import TooltipInfo from '../../../partials/TooltipInfo';
import { PatternFormat } from 'react-number-format';
import InputField from '../../../partials/form_fields/InputField';
import SelectItems from '../../../partials/form_fields/SelectItems';
import selectStyle from '../../../partials/form_fields/selectStyle';
import InputImage from '../../../partials/form_fields/InputImage';
import IconDiscount from '../../../../assets/images/icons/icon-discount.svg'
import IconGift from '../../../../assets/images/icons/icon-gift-solid.svg'
import IconPunch from '../../../../assets/images/icons/icon-punch.svg'
import styles from './edit-product.module.scss';

const availabilityOptions = [
  { label: "De suite", value: "right now" },
  { label: "Autre", value: "other" },
];

// Correspondance "Famille de produit" -> numéro de dossier (fileNumber)
// Les labels doivent matcher ceux présents dans `company.products`.
const fileNumberByProductLabel = {
  'IMAGE': '821028',
  'AUDIO': '821029',
  'INFORMATIQUE': '821023',
  'PHOTO': '821023',
  'TELEPHONIE': '821026',
  'GPS': '821023',
  'ACCESSOIRES / OBJETS CO': '821027',
  'PEM': '821030',
  'GEM': '821031',
  'MOBILITE URBAINE': '821027',
};

function resolveFileNumber(productLabel) {
  if (!productLabel) return null;
  const normalized = String(productLabel).trim().toUpperCase().replace(/\s+/g, ' ');
  // Passage par une version "compactée" pour être tolérant aux espaces multiples.
  return fileNumberByProductLabel[normalized] ?? fileNumberByProductLabel[productLabel] ?? null;
}

const Zoom = cssTransition({
  enter: 'zoomInToast',
  exit: 'zoomOutToast',
  duration: 600,
  appendPosition: false,
});

const EditProduct = ({ history }) => {
  const params = useParams();
  const [context, dispatch] = useGlobalContext();
  const [formData, setFormData] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [init, setInit] = useState(false);
  const [customerBenefit, setCustomerBenefit] = useState();
  const {
    companyReducer,
    productsReducer,
  } = context;
  const { product, isLoading, error } = productsReducer;
  const { company } = companyReducer;

  //ACTIONS
  const _getProductByID = (id) => getProductByID(dispatch, id);
  const _postProduct = (product) => postProduct(dispatch, product);
  const _putProduct = (product) => putProduct(dispatch, product);
  const _deleteProduct = (id) => deleteProduct(dispatch, id);
  const _resetSelectedProduct = () => resetSelectedProduct(dispatch);
  const _resetErrorProducts = () => resetErrorProducts(dispatch);

  // TOAST
  const notifyCreate = () => toast('Le produit a bien été créé', { autoClose: 2000, transition: Zoom, className: 'success' });
  const notifyUpdate = () => toast('Le produit a bien été modifié', { autoClose: 2000, transition: Zoom, className: 'success' });
  
  useEffect(() => {
    if (!params.id) {
      setInit(true);
      return;
    };
    _getProductByID(params.id);
  }, [params.id]);

  useEffect(() => {
    return () => {
      _resetSelectedProduct();
      _resetErrorProducts();
    }
  }, []);

  useEffect(() => {
    if (!product) return;

    // Convertir les boxProducts pour s'assurer que les valeurs numériques sont des nombres
    const boxProducts = (product?.boxProducts || []).map(item => ({
      ...item,
      quantity: item.quantity ? Number(item.quantity) : '',
      pricePA: item.pricePA ? Number(item.pricePA) : '',
      deee: item.deee !== undefined && item.deee !== null && item.deee !== '' ? Number(item.deee) : ''
    }));

    setFormData({
      ref: product.ref,
      brand: product?.brand || '',
      gencod: product.gencod,
      description: product.description,
      product: product.product,
      fileNumber: product?.fileNumber ?? null,
      packing: product.packing || null,
      price: product.price,
      pricePA: product?.pricePA || '',
      box: product?.box || '',
      boxProducts: boxProducts,
      availability: product.availability,
      availabilityComment: product?.availabilityComment || '',
      deee: product?.deee,
      tax: product?.tax,
      gift: product?.gift || '',
      punch: product?.punch || '',
      discount: product?.discount || '',
    });

    if (product.discount) {
      setCustomerBenefit('discount');
    } else if (product.gift) {
      setCustomerBenefit('gift');
    } else if (product.punch) {
      setCustomerBenefit('punch');
    }
  
    setInit(true);

  }, [productsReducer.product]);

  useEffect(() => {
    const tax = Number.parseFloat(formData?.tax);
    const deee = Number.parseFloat(formData?.deee);

    const boxValid = formData?.packing !== 'box' || 
      (formData?.box && formData.box.trim() !== '') ||
      (formData?.boxProducts && Array.isArray(formData.boxProducts) && formData.boxProducts.length > 0 &&
       formData.boxProducts.every(item => 
         item.gencod && item.gencod.length === 13 &&
         item.libelle && item.libelle.trim() !== '' &&
         item.quantity && Number(item.quantity) > 0 &&
         item.pricePA && Number(item.pricePA) >= 0 &&
         (item.deee !== undefined && item.deee !== null && !Number.isNaN(Number(item.deee)))
       ));
    if (
      formData?.ref &&
      (formData?.gencod && formData?.gencod.length === 13) &&
      formData?.description &&
      ((formData?.packing !== 'box' && formData?.packing) || boxValid) &&
      formData?.price &&
      formData?.product &&
      formData?.availability &&
      (formData?.image || product?.image) &&
      (formData?.file || product?.file) &&
      (typeof tax === "number" && !Number.isNaN(tax)) &&
      (typeof deee === "number" && !Number.isNaN(deee)) &&
      (
        (customerBenefit === 'gift' && formData?.gift)
        || (customerBenefit === 'punch' && formData?.punch)
        || (customerBenefit === 'discount' && formData?.discount)
      )
    ) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }

  }, [formData, customerBenefit]);

  const submit = async () => {
    if (product && !['pending', 'correction'].includes(product?.status)) return;
    if (!isValid) return;
    
    const data = { ...formData };
    try {
      if (product) {
        const response = await _putProduct({
          _id: product._id,
          ...data
        });
        if (response) {
          notifyUpdate();
        }
      } else {
        const response = await _postProduct({
          company: companyReducer.company._id,
          ...data
        });
        if (response && response.message === 'product saved') {
          notifyCreate();
          history.push('/products-provider');
        }
      }
    } catch (err) {
      // Les erreurs sont gérées par le reducer
      console.error('Error submitting product:', err);
    }
  };

  const handleChange = (val, error, field) => {
    _resetErrorProducts();
    let updatedData = { ...formData };
    // Validation pour le champ "ref" : longueur exacte de 19 caractères
    if (field === 'ref') {
      const refLength = val ? val.length : 0;
      if (refLength > 19) {
        error = true;
      }
    }
    
    if (error) {
      _.unset(updatedData, field, val);
    } else {
      _.set(updatedData, field, val);
    }

    if (field === 'packing') {
      if (val !== 'box') {
        _.set(updatedData, 'box', '');
        _.set(updatedData, 'boxProducts', []);
      } else if (!updatedData.boxProducts) {
        _.set(updatedData, 'boxProducts', []);
      }
    }

    if (field === 'availability' && val !== 'other') {
      _.set(updatedData, 'availabilityComment', '');
    }
    console.log(updatedData)
    setFormData(updatedData)
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(null);
  });

  const handleChangeImageForImage = useCallback(async (img) => {
    if (product && !['pending', 'correction'].includes(product?.status)) return;
    _resetErrorProducts();
    const base64 = await toBase64(img);
    if (base64) handleChange(base64, null, 'image');
  }, [product, handleChange]);

  const handleChangeImageForFile = useCallback(async (img) => {
    if (product && !['pending', 'correction'].includes(product?.status)) return;
    _resetErrorProducts();
    const base64 = await toBase64(img);
    if (base64) handleChange(base64, null, 'file');
  }, [product, handleChange]);

  const handleDeleteForImage = useCallback(async () => {
    if ((product && !['pending', 'correction'].includes(product?.status)) || !product) return;
    _resetErrorProducts();
    handleChange(null, null, 'image');
  }, [product, handleChange]);

  const handleDeleteForFile = useCallback(async () => {
    if ((product && !['pending', 'correction'].includes(product?.status)) || !product) return;
    _resetErrorProducts();
    handleChange(null, null, 'file');
  }, [product, handleChange]);

  const handleDeleteProduct = async () => {
    if (product && !['pending', 'correction'].includes(product?.status)) return;
    const response = await _deleteProduct(product._id);
    if (response.status === 200 ) history.push('/products-provider');
  };

  const handleCustomerBenefit = (benefit) => {
    if (product && (product.status === 'valid' || product.status === 'refused')) return;

    _resetErrorProducts();

    let updatedData = { ...formData };
    setCustomerBenefit(benefit);

    _.set(updatedData, 'discount', '');
    _.set(updatedData, 'punch', '');
    _.set(updatedData, 'gift', '');

    setFormData(updatedData)
  };

  const handleAddBoxProduct = () => {
    if (product && (product.status === 'valid' || product.status === 'refused')) return;
    _resetErrorProducts();
    
    const updatedData = { ...formData };
    const boxProducts = updatedData.boxProducts || [];
    boxProducts.push({
      gencod: '',
      libelle: '',
      quantity: '',
      pricePA: '',
      deee: ''
    });
    _.set(updatedData, 'boxProducts', boxProducts);
    setFormData(updatedData);
  };

  const handleRemoveBoxProduct = (index) => {
    if (product && (product.status === 'valid' || product.status === 'refused')) return;
    _resetErrorProducts();
    
    const updatedData = { ...formData };
    const boxProducts = [...(updatedData.boxProducts || [])];
    boxProducts.splice(index, 1);
    _.set(updatedData, 'boxProducts', boxProducts);
    setFormData(updatedData);
  };

  const handleBoxProductChange = (index, field, value) => {
    if (product && (product.status === 'valid' || product.status === 'refused')) return;
    _resetErrorProducts();
    
    const updatedData = { ...formData };
    const boxProducts = [...(updatedData.boxProducts || [])];
    if (boxProducts[index]) {
      // Convertir les champs numériques en nombres
      let processedValue = value;
      if (['quantity', 'pricePA', 'deee'].includes(field)) {
        if (value === '' || value === null || value === undefined) {
          processedValue = '';
        } else {
          const numValue = Number.parseFloat(value);
          processedValue = Number.isNaN(numValue) ? value : numValue;
        }
      }
      
      boxProducts[index] = { ...boxProducts[index], [field]: processedValue };
      _.set(updatedData, 'boxProducts', boxProducts);
      setFormData(updatedData);
    }
  };

  const errorMessage = useMemo(() => {
    let message = error;
    if (error === 'This product gencod is already in use.') {
      message = 'Ce gencod est déjà utilisé.';
    }
    return message;
  }, [error]);

  const defaultImageValue = useMemo(() => {
    if (!product?.image || !product?.image?.path) return null;
    const uri = `${process.env.REACT_APP_API_URL}/files/${product.image.path.replace('upload/','')}`;
    return uri;
  }, [product?.image]);

  const defaultFileValue = useMemo(() => {
    if (!product?.file || !product?.file?.path) return null;
    const uri = `${process.env.REACT_APP_API_URL}/files/${product.file.path.replace('upload/','')}`;
    return uri;
  }, [product?.file]);

  const optionsProduct = useMemo(() => company.products.map((product) => ({ label: product, value: product })), [company]);
  const invalidReasons = useMemo(() => {
    const reasons = [];
    const tax = Number.parseFloat(formData?.tax);
    const deee = Number.parseFloat(formData?.deee);
    const boxValid = formData?.packing !== 'box'
      || (formData?.box && formData.box.trim() !== '')
      || (formData?.boxProducts && Array.isArray(formData.boxProducts) && formData.boxProducts.length > 0
        && formData.boxProducts.every((item) =>
          item.gencod && item.gencod.length === 13
          && item.libelle && item.libelle.trim() !== ''
          && item.quantity && Number(item.quantity) > 0
          && item.pricePA && Number(item.pricePA) >= 0
          && (item.deee !== undefined && item.deee !== null && !Number.isNaN(Number(item.deee)))
        ));

    if (!formData?.ref) reasons.push('Référence produit manquante');
    if (!(formData?.gencod && formData?.gencod.length === 13)) reasons.push('Gencod invalide (13 caractères requis)');
    if (!formData?.description) reasons.push('Désignation du produit manquante');
    if (!((formData?.packing !== 'box' && formData?.packing) || boxValid)) reasons.push('Colisage / détails box incomplets');
    if (!formData?.price) reasons.push('Prix net salon manquant');
    if (!formData?.product) reasons.push('Famille de produit manquante');
    if (!formData?.availability) reasons.push('Disponibilité produit manquante');
    if (!(formData?.image || product?.image)) reasons.push('Visuel produit manquant');
    if (!(formData?.file || product?.file)) reasons.push('Fiche produit manquante');
    if (!(typeof tax === "number" && !Number.isNaN(tax))) reasons.push('Autres taxes invalides');
    if (!(typeof deee === "number" && !Number.isNaN(deee))) reasons.push('DEEE invalide');
    if (!(customerBenefit === 'discount' && formData?.discount)) reasons.push('Avantage (promotion) manquant');

    return reasons;
  }, [formData, product, customerBenefit]);

  // handle value discount
  useEffect(() => {
    if (formData.price && formData.pricePA && customerBenefit === 'discount') {
      let discount = 100 - (formData.price * 100) / formData.pricePA;
      discount = parseFloat(discount).toFixed(2);
      if (discount < 0) discount = '0';
      handleChange(discount, false, "discount");
    } else {
      handleChange('', false, "discount");
    }
  }, [
    formData.price,
    formData.pricePA,
    customerBenefit
  ]);

  return (
    <div className={styles.formProduct}>
      <div className="content large">
        <div className={styles.header}>
          <h1>Ajout d'un nouveau produit</h1>
          {(
            !product ||
            product?.status && ['pending', 'correction'].includes(product.status)
          ) && (
            <button
              className={!isValid ? "disabled" : ''}
              onClick={() => submit()}
              title={!isValid ? `Impossible de valider:\n- ${invalidReasons.join('\n- ')}` : ''}
            >
              {product ? 'Modifier' : 'Ajouter'}
              {isLoading && (
                <div className={styles.loader}></div>
              )}
            </button>
          )}
        </div>
        {errorMessage && (
          <p className={styles.error}>{errorMessage}</p>
        )}
        {((!params.id && init) || (params.id && product && init)) && (
          <div className={styles.form}>
            <div className={styles.info}>
              <div className={styles.row}>
              <h4>Info</h4>
              {product && (
                <p className={`${styles.status} ${styles[product.status]}`}>
                  {product.status === 'pending' && 'En attente'}
                  {product.status === 'valid' && 'Validée'}
                  {product.status === 'refused' && 'Refusée'}
                  {product.status === 'correction' && 'À corriger'}
                </p>
              )}
              </div>
              <div className={styles.row}>
                <InputField
                  id='ref'
                  defaultValue={formData.ref || ""}
                  title="Référence produit"
                  placeholder=""
                  textTransform="uppercase"
                  type="string"
                  minLength={19}
                  maxLength={19}
                  required={true}
                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                  handleChange={(val, error) => handleChange(val, error, "ref")}
                />
                <div className={styles.selectField}>
                  <label>Famille de produit*</label>
                  <Select
                    onChange={(val) => {
                      const nextProductLabel = val?.value ?? null;
                      const nextFileNumber = nextProductLabel ? resolveFileNumber(nextProductLabel) : null;

                      // Update atomique: éviter le problème de double `handleChange`
                      // qui peut écraser une des deux valeurs avec l'ancien `formData`.
                      _resetErrorProducts();
                      setFormData((prev) => {
                        const updated = { ...prev };
                        _.set(updated, 'product', nextProductLabel);
                        _.set(updated, 'fileNumber', nextFileNumber);
                        return updated;
                      });
                    }}
                    options={optionsProduct}
                    value={!formData?.product ? null : optionsProduct.filter((d) => d.value === formData?.product)}
                    isSearchable={false}
                    styles={selectStyle}
                    isClearable
                    isDisabled={product && (product.status === 'valid' || product.status === 'refused')}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <InputField
                  id='brand'
                  defaultValue={formData.brand || ""}
                  title="Marque"
                  placeholder=""
                  type="string"
                  textTransform='uppercase'
                  maxLength={30}
                  required={true}
                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                  handleChange={(val, error) => handleChange(val, error, "brand")}
                />
                <div className={`container-input ${styles.gencod}`}>
                  <label>Gencod*</label>
                  <PatternFormat
                    id='gencod'
                    defaultValue={formData.gencod || ""}
                    format="#############"
                    placeholder='_____________'
                    mask='_'
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    onChange={(e) => {
                      handleChange(e.target.value.trim(), !e.target.value, "gencod");
                    }}
                  />
                </div>

              </div>
              <div className={styles.row}>
                <div className={`container-input`}>
                  <label>
                    Visuel produit*
                    <TooltipInfo text={
                      `L'image du produit doit être au format .png, .jpg ou .jpeg et avoir un poids maximum de 1Mo.`
                    }/>
                  </label>
                  <InputImage
                    id='image'
                    defaultValue={defaultImageValue}
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChangeImage={handleChangeImageForImage}
                    handleDelete={handleDeleteForImage}
                    placeholder={"Cliquez pour déposer le visuel produit au format .jpg ou .png"}
                    accept='image/png, image/jpeg'
                  />
                </div>
                <div className={`container-input`}>
                  <label>
                    Fiche produit*
                    <TooltipInfo text={
                      `La fiche du produit doit être au format .png, .jpg, .jpeg ou .pdf et avoir un poids maximum de 1Mo.`
                    }/>
                  </label>
                  <InputImage
                    id='file'
                    defaultValue={defaultFileValue}
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChangeImage={handleChangeImageForFile}
                    handleDelete={handleDeleteForFile}
                    placeholder={"Cliquez pour déposer la fiche produit au format .jpg, .png ou .pdf"}
                    accept='image/png, image/jpeg, application/pdf'
                    allowPdf
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div>
                  <label>Designation du produit*</label>
                  <textarea
                    rows="5" cols="63"
                    placeholder='Libellé et caractéristiques principales'
                    value={formData.description || ""}
                    maxLength='100'
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    onChange={(e) => {
                      handleChange(e.target.value, null, "description");
                    }}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.selectItemsFields}>
                  <label>Colisage</label>
                  <SelectItems
                    items={["Colis", "Box"]}
                    selectedItems={formData.packing === 'box' ? ['Box'] : ((formData.packing === '' || (formData.packing && formData.packing !== 'box')) ? ["Colis"] : [])}
                    uniq={true}
                    handleChange={(val) => {
                      if(product && product.status === 'valid') return
                      if (val === 'Box') handleChange('box', false, 'packing')
                      else handleChange('', false, 'packing')
                    }}
                  />
                </div>
              </div>
              <div className={styles.row}>
                {(formData.packing === '' || (formData.packing && formData.packing !== 'box')) &&
                  <InputField
                    id='packing'
                    defaultValue={formData.packing || ""}
                    title="Nbre d'UVC par colis"
                    placeholder=""
                    type="number"
                    allowEmpty={true}
                    required={true}
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChange={(val, error) => handleChange(val, error, "packing")}
                  />
                }

                {(formData.packing === 'box') &&  
                  <div className={styles.box}>
                    <label>Détails box*</label>
                    <div className={styles.boxProductsTable}>
                      <table>
                        <thead>
                          <tr>
                            <th>Gencod*</th>
                            <th>Libellé*</th>
                            <th>Quantité*</th>
                            <th>PA unitaire HT*</th>
                            <th>DEEE/Ecomobilier HT*</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.boxProducts || []).map((item, index) => (
                            <tr key={index}>
                              <td>
                                <PatternFormat
                                  format="#############"
                                  placeholder='_____________'
                                  mask='_'
                                  value={item.gencod || ''}
                                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                                  onChange={(e) => {
                                    handleBoxProductChange(index, 'gencod', e.target.value.trim());
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={item.libelle || ''}
                                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                                  onChange={(e) => {
                                    handleBoxProductChange(index, 'libelle', e.target.value);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={item.quantity || ''}
                                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                                  onChange={(e) => {
                                    handleBoxProductChange(index, 'quantity', e.target.value);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.pricePA || ''}
                                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                                  onChange={(e) => {
                                    handleBoxProductChange(index, 'pricePA', e.target.value);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.deee || ''}
                                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                                  onChange={(e) => {
                                    handleBoxProductChange(index, 'deee', e.target.value);
                                  }}
                                />
                              </td>
                              <td>
                                {!product || ['pending', 'correction'].includes(product.status) ? (
                                  <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => handleRemoveBoxProduct(index)}
                                  >
                                    <MdDeleteForever size={20} />
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!product || ['pending', 'correction'].includes(product.status)) && (
                        <button
                          type="button"
                          className={styles.addButton}
                          onClick={handleAddBoxProduct}
                        >
                          + Ajouter un article
                        </button>
                      )}
                    </div>
                    <textarea
                      rows="5" cols="63"
                      placeholder='Informations complémentaires'
                      value={formData.box || ""}
                      disabled={product && (product.status === 'valid' || product.status === 'refused')}
                      onChange={(e) => {
                        handleChange(e.target.value, null, "box");
                      }}
                    />
                  </div>
                }
              </div>
            </div>
            <div className={styles.price}>
              <h4>Tarif HT</h4>
              <div className={styles.row}>
                <InputField
                  id='price'
                  defaultValue={formData.price || ""}
                  title="Prix net salon"
                  placeholder=""
                  type="number"
                  step='any'
                  allowEmpty={true}
                  decimalScale={2}
                  required={true}
                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                  handleChange={(val, error) => {
                    handleChange(val, error, "price");
                  }}
                />
                <InputField
                  id='pricePA'
                  defaultValue={formData.pricePA || ""}
                  title="PA permanent (si permanent)"
                  placeholder=""
                  type="number"
                  step='any'
                  allowEmpty={true}
                  decimalScale={2}
                  required={false}
                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                  handleChange={(val, error) => handleChange(val, error, "pricePA")}
                />
              </div>
              <div className={styles.row}>
                <InputField
                  id='deee'
                  defaultValue={formData.deee || ""}
                  title="DEEE"
                  placeholder=""
                  type="number"
                  step='any'
                  allowEmpty={true}
                  decimalScale={2}
                  required={true}
                  disabled={product && (product.status === 'valid' || product.status === 'refused')}
                  handleChange={(val, error) => handleChange(val, error, "deee")}
                />
                <div className={styles.tax}>
                  <label>
                    Autres taxes*
                    <TooltipInfo text={
                      `A renseigner pour les taxes Eco mobilier et/ou SORECOP. Indiquer 0 si pas d'autre taxe`
                    }/>
                  </label>
                  <InputField
                    id='tax'
                    defaultValue={formData.tax || ""}
                    title=""
                    placeholder=""
                    type="number"
                    step='any'
                    allowEmpty={true}
                    decimalScale={2}
                    required={true}
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChange={(val, error) => handleChange(val, error, "tax")}
                  />
                </div>
              </div>
              <div className={styles.more}>
                <label>Avantage</label>
                <ul className={product && (product.status === 'valid' || product.status === 'refused') ? styles.disabled : ''}>
                  <li>
                    <div
                      onClick={() => handleCustomerBenefit('gift')}
                      className={customerBenefit === 'gift' ? `${styles.circle} ${styles.selected}` : styles.circle}
                    >
                      <img src={IconGift} alt="cadeau" />
                    </div>
                    <p onClick={() => handleCustomerBenefit('gift')}>
                      Cadeau
                    </p>
                  </li>
                  <li>
                    <div
                      className={customerBenefit === 'punch' ? `${styles.circle} ${styles.selected}` : styles.circle}
                      onClick={() => handleCustomerBenefit('punch')}
                    >
                      <img src={IconPunch} alt="offre cout de poing" />
                    </div>
                    <p onClick={() => handleCustomerBenefit('punch')}>
                      Coup de poing
                    </p>
                  </li>
                  <li>
                    <div
                      className={customerBenefit === 'discount' ? `${styles.circle} ${styles.selected}` : styles.circle}
                      onClick={() => handleCustomerBenefit('discount')}
                    >
                      <img src={IconDiscount} alt="promotion" />
                    </div>
                    <p onClick={() => handleCustomerBenefit('discount')}>
                      Promotion
                    </p>
                  </li>
                </ul>
                {customerBenefit === 'gift' && (
                  <InputField
                    id='gift'
                    defaultValue={formData.gift || ""}
                    title="Cadeau"
                    placeholder=""
                    type="string"
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChange={(val, error) => handleChange(val, error, "gift")}
                  />
                )}
                {customerBenefit === 'punch' && (
                  <InputField
                    id='punch'
                    defaultValue={formData.punch || ""}
                    title="Coup de poing"
                    placeholder=""
                    type="string"
                    disabled={product && (product.status === 'valid' || product.status === 'refused')}
                    handleChange={(val, error) => handleChange(val, error, "punch")}
                  />
                )}
                {customerBenefit === 'discount' && (
                  <InputField
                    id='discount'
                    defaultValue={formData.discount || ""}
                    value={formData.discount}
                    title="Pourcentage de réduction"
                    placeholder=""
                    type="number"
                    step='any'
                    allowEmpty={true}
                    decimalScale={2}
                    required={true}
                    disabled={true}
                  />
                )}
              </div>
            </div>
            <div className={styles.availability}>
              <h4>Disponibilité</h4>
              <div className={styles.row}>
                <div className={styles.selectField}>
                  <label>Disponibilité produit*</label>
                  <Select
                    onChange={(val) => {
                      handleChange(val?.value, null, "availability");
                    }}
                    options={availabilityOptions}
                    value={!formData?.availability ? null : availabilityOptions.filter((d) => d.value === formData?.availability)}
                    isSearchable={false}
                    styles={selectStyle}
                    isClearable
                    isDisabled={product && (product.status === 'valid' || product.status === 'refused')}
                  />
                </div>
              </div>
              <div className={styles.row}>
                {formData.availability === 'other' && (
                  <div className={styles.availabilityComment}>
                    <label>Délai de livraison</label>
                    <textarea
                      rows="5" cols="63"
                      value={formData.availabilityComment || ""}
                      disabled={product && (product.status === 'valid' || product.status === 'refused')}
                      onChange={(e) => {
                        handleChange(e.target.value, null, "availabilityComment");
                      }}
                    />
                  </div>
                )} 
              </div>
            </div>
          </div>
        )}
        {product && ['pending', 'correction'].includes(product.status) && (
          <div className={styles.delete}>
            <p onClick={() => handleDeleteProduct()}>
              <MdDeleteForever size={20} />
              <span>Supprimer l'article</span>
            </p>
          </div>
        )}
      </div>
      <ToastContainer
        position="bottom-right"
        hideProgressBar={true}
      />
    </div>
  )
};

export default EditProduct;
